import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import {
  Camera, Mic, FileText, Upload, Loader2, Image as ImageIcon, Video, Volume2, MapPin, Globe,
} from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';

const TYPE_ICON = {
  screenshot: ImageIcon, annotated_screenshot: ImageIcon, screen_recording: Video,
  gif: ImageIcon, voice_note: Volume2, transcript: FileText, console_log: FileText,
  network_log: FileText, file: FileText, code_reference: FileText,
  documentation_reference: FileText, note: FileText,
};

const TYPE_COLOR = {
  screenshot: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
  annotated_screenshot: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
  screen_recording: 'bg-purple-500/10 text-purple-700 dark:text-purple-300',
  voice_note: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  note: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
};

export const EvidenceTimeline = ({ sessionId, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteTitle, setNoteTitle] = useState('');

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await tl.listEvidence(sessionId);
      setItems(data.items || []);
    } catch (e) {
      toast.error(`Evidence load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleFileUpload = async (file, evidenceType) => {
    if (!file) return;
    setUploading(true);
    try {
      const t = toast.loading(`Uploading ${file.name}…`);
      await tl.uploadEvidence(sessionId, file, {
        evidence_type: evidenceType,
        url: window.location.href,
        route: window.location.pathname,
      });
      toast.dismiss(t);
      toast.success(`${evidenceType === 'voice_note' ? 'Voice note transcribed' : 'Uploaded'}`);
      await load();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await tl.addNoteEvidence(sessionId, {
        title: noteTitle || 'Observation',
        note: noteText,
        url: window.location.href,
        route: window.location.pathname,
      });
      setNoteText('');
      setNoteTitle('');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const fileFor = (accept, type) => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = accept;
    inp.onchange = (e) => handleFileUpload(e.target.files[0], type);
    inp.click();
  };

  return (
    <Card className="flex flex-col h-full" data-testid="evidence-timeline">
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold">Evidence Timeline</h3>
            <p className="text-[11px] text-muted-foreground">{items.length} item{items.length !== 1 ? 's' : ''}</p>
          </div>
          {uploading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          <Button
            variant="outline" size="sm" className="h-8 text-[11px]"
            onClick={() => fileFor('image/*', 'screenshot')}
            disabled={uploading}
            data-testid="evidence-upload-screenshot"
          >
            <Camera className="w-3 h-3 mr-1" /> Image
          </Button>
          <Button
            variant="outline" size="sm" className="h-8 text-[11px]"
            onClick={() => fileFor('video/*', 'screen_recording')}
            disabled={uploading}
            data-testid="evidence-upload-video"
          >
            <Video className="w-3 h-3 mr-1" /> Video
          </Button>
          <Button
            variant="outline" size="sm" className="h-8 text-[11px]"
            onClick={() => fileFor('audio/*', 'voice_note')}
            disabled={uploading}
            data-testid="evidence-upload-voice"
          >
            <Mic className="w-3 h-3 mr-1" /> Voice
          </Button>
          <Button
            variant="outline" size="sm" className="h-8 text-[11px]"
            onClick={() => fileFor('*', 'file')}
            disabled={uploading}
            data-testid="evidence-upload-file"
          >
            <Upload className="w-3 h-3 mr-1" /> File
          </Button>
        </div>

        <div className="mt-3 space-y-1.5">
          <Input
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            placeholder="Observation title (optional)"
            className="h-7 text-xs"
            data-testid="evidence-note-title"
          />
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add quick observation…"
            className="min-h-[50px] text-xs resize-none"
            data-testid="evidence-note-text"
          />
          <Button
            size="sm" className="h-7 text-xs w-full"
            onClick={addNote}
            disabled={!noteText.trim()}
            data-testid="evidence-note-add"
          >
            Add note to timeline
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {loading ? (
            <div className="text-center py-8 text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-xs text-muted-foreground">
              No evidence yet. Upload a screenshot, voice note, or add an observation.
            </div>
          ) : (
            items.map((ev) => {
              const Icon = TYPE_ICON[ev.evidence_type] || FileText;
              const color = TYPE_COLOR[ev.evidence_type] || 'bg-muted text-foreground';
              return (
                <div
                  key={ev.evidence_id}
                  className="border border-border rounded-md p-2.5 space-y-1.5 hover:border-violet-500/50 transition-colors"
                  data-testid={`evidence-${ev.evidence_id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-7 h-7 rounded ${color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium truncate">{ev.title || ev.evidence_type}</span>
                        <Badge variant="outline" className="text-[9px] h-4">{ev.evidence_type}</Badge>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(ev.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                  {ev.note && (
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap pl-9">{ev.note}</p>
                  )}
                  {ev.transcript && (
                    <div className="pl-9 text-xs italic text-muted-foreground border-l-2 border-emerald-500/40 ml-3 pl-3">
                      "{ev.transcript}"
                    </div>
                  )}
                  {ev.storage_ref && ev.evidence_type.includes('screenshot') && (
                    <a href={ev.storage_ref} target="_blank" rel="noreferrer" className="block pl-9">
                      <img src={ev.storage_ref} alt={ev.title} className="max-h-32 rounded border border-border" />
                    </a>
                  )}
                  {ev.storage_ref && (ev.evidence_type === 'voice_note' || ev.evidence_type === 'screen_recording') && (
                    <div className="pl-9">
                      {ev.evidence_type === 'voice_note' ? (
                        <audio src={ev.storage_ref} controls className="w-full h-8" />
                      ) : (
                        <video src={ev.storage_ref} controls className="max-h-40 rounded border border-border" />
                      )}
                    </div>
                  )}
                  {(ev.url || ev.route) && (
                    <div className="pl-9 text-[10px] text-muted-foreground flex items-center gap-2">
                      {ev.route && <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {ev.route}</span>}
                      {ev.url && <span className="flex items-center gap-1 truncate"><Globe className="w-2.5 h-2.5" /> {new URL(ev.url).host}</span>}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </Card>
  );
};

export default EvidenceTimeline;
